import { ActivatedRoute } from "@angular/router";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { GridApi, IServerSideDatasource, IServerSideGetRowsParams } from "@ag-grid-community/core";
import { GridOptions, Module } from "@ag-grid-community/core";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { ServerSideRowModelModule } from "@ag-grid-enterprise/server-side-row-model";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { filter, switchMap, takeUntil } from "rxjs/operators";
import { Subject } from 'rxjs';
import { CreateVendorItemComponent } from "src/app/components/dialogs/create-vendor-item/create-vendor-item.component";
import { MapSkuToProductComponent } from "src/app/components/dialogs/map-sku-to-product/map-sku-to-product.component";
import { OrdersGridConfigService } from "src/app/data-grid/orders-grid-config.service";
import { historyColumnDefs,} from "./../detail-components/history-grid-options";
import { listingColumnDefs,} from '../detail-components/listings-grid-options';
import {  poColumnDefs,} from "../detail-components/po's-grid-options";
import {  columnDefs,} from "./../detail-components/vendors-grid-options";
import { PlanyardService } from "../../planyard/planyard.service";
import {  SaveItemPayload,  SetSkuMapPayload,} from "./../../../models/planyard.model";
import { InventoryService } from '../../../services/inventory.service';
import { SideNavService } from '../../../services/sidenav.service';
import { DialogActions } from "./../../../components/dialogs/dialog.model";
import { ConfirmDialogComponent } from '../../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { SnackbarComponent } from '../../../components/snackbar/snackbar.component';

@Component({
  selector: "app-product-detail",
  templateUrl: "./product-detail.component.html",
  styleUrls: ["./product-detail.component.scss"],
})
export class ProductDetailComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private _activeTab: string;
  gridOptions: GridOptions;
  columnDefs;
  modules: Module[] = [ServerSideRowModelModule];
  gridApi: GridApi;
  actions: DialogActions[];
  productId: number;
  title: string;
  product: any = {};
  apiLink;
  gridRequestParams;
  page: boolean;
  spin: boolean = false;

  productForm: FormGroup = new FormGroup({
    name: new FormControl("", [Validators.required]),
    title: new FormControl(""),
    imageUrl: new FormControl(""),
    brand: new FormControl(""),
    model: new FormControl(""),
    color: new FormControl(""),
    upc: new FormControl(""),
    weight: new FormControl(""),
    width: new FormControl(""),
    height: new FormControl(""),
    length: new FormControl(""),
    notes: new FormControl(""),
    defaultLocation: new FormControl("")
  });
  get activeTab() {
    return this._activeTab;
  }

  set activeTab(tab: string) {
    this._activeTab = tab;
    this.selectPage(this._activeTab);
  }

  constructor(
    private planyardService: PlanyardService,
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<ProductDetailComponent>,
    private inventoryService: InventoryService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private sideNavService: SideNavService,
    private ordersGridConfigService: OrdersGridConfigService
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.params["product"]) {
      this.page = true;
      this.data = {
        ItemId: this.route.snapshot.params["product"],
      };
    }
    if (this.data) {
      this.productId = this.data.ItemId;
      this.loadProduct(this.productId);
      this.title = `Edit Product ${this.productId}`;
      this.activeTab = "SKUs";
    } else {
      this.title = "New Product";
    }

    const productNameInput = this.productForm.get("name");

    productNameInput.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        switchMap((e) => this.planyardService.ProductExists(e.trim()))
      )
      .subscribe((e) => {
        if (e) {
          if (!this.product || this.product.name !== productNameInput.value) {
            productNameInput.setErrors({ unique: true });
          }
        } else {
          productNameInput.setErrors(null);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  loadProduct(id) {
      this.planyardService.GetProductDetails(id).pipe(takeUntil(this.destroy$)).subscribe((e: any) => {
      this.productId = e.id;
      this.fillForm(e);
      this.title = e.name;
      this.product = e;
      this.activeTab = "SKUs";
    });
  }

  saveProduct() {
    this.spin = true;
    const payload: SaveItemPayload = {
      ...this.productForm.value,
      name: this.productForm.value.name.trim(),
      length: +this.productForm.value.length,
      height: +this.productForm.value.height,
      width: +this.productForm.value.width,
      weight: +this.productForm.value.weight,
      vendorItems: [],
    };
    if (this.productId) {
      payload["id"] = this.productId;
    }

    this.planyardService.SaveItem(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (e) => {
        this.spin = false;
        this.snackBar.openFromComponent(SnackbarComponent,
            {
              duration: 3000,
              horizontalPosition: 'center',
              data: {
                title: 'Product saved successfully',
                action: 'Dismiss',
                type: 'success',
              },
            }
        );
        let obj : any ={
          itemId: e,
          name: this.productForm.get('name').value
        }
        this.dialogRef.close(!this.productId ? obj : "refresh");
        this.sideNavService.sidenavData$.next({ItemId: this.productId ? this.productId : e, redirectToBasicInfo: true});
        this.sideNavService.toggle();
      },
     error: (err) => {
      this.spin = false
     }
   });

  }

  fillForm(e) {
    this.productForm.patchValue(e);
  }

  tabClicked(e) {
    this.activeTab = e;
  }

  selectPage(page) {
    let skuIds;
    if (page) {
      skuIds = this.data?.AccountSkuIds
        ? this.data.AccountSkuIds.split(",").map(Number)
        : [];
    }
    if (page == "Vendor Items") {
      const itemsColumns = [...columnDefs, ...[{
        field: "",
        headerName: '',
        cellRenderer: "actionsComponent",
        cellRendererParams: {
          edit: (params) => this.editItem(params),
          delete: (params) => this.deleteItem(params),
        },
      }]];
      this.columnDefs = itemsColumns;
      const filter = {
        ItemId: {
          filterType: "number",
          type: "in",
          intValues: [this.productId],
        },
        DeleteFlag: {
          filterType: "boolean",
          value: false,
        },
      }
      this.apiLink = this.planyardService.GetProductsItemsList(
        this.columnDefs,
        filter
      );
    } else if (page == "SKUs") {
      const listingColumns = [...listingColumnDefs, ...[{
        field: "Actions",
        headerName: '',
        cellRenderer: "actionsComponent",
        cellRendererParams: {
          edit: (params) => this.editMapping(params),
        },
      }]];
      this.columnDefs = listingColumns;
      this.apiLink = this.planyardService.GetProductMappings(this.data.ItemId);
    } else if (page == "Orders") {
      this.columnDefs = this.ordersGridConfigService.columnDefs;
      this.apiLink = this.planyardService.GetProductsOrderList(
        this.columnDefs,
        skuIds
      );
    } else if (page == "History") {
      this.columnDefs = historyColumnDefs;
      this.apiLink = this.planyardService.GetInventoryTransationList(
        this.gridRequestParams,
        this.columnDefs,
        this.productId
      );
    } else {
      this.columnDefs = poColumnDefs;
      this.apiLink = this.inventoryService.GetProductPos(this.productId);
    }
    this.gridApi?.refreshServerSide({ route: [], purge: true });
  }

  gridReady(event) {
    this.gridApi = event.api;
    this.gridApi.setGridOption("serverSideDatasource", this.vendorDataSource);
  }

  action(e) {
    if (e === "Save") {
      this.saveProduct();
    } else if (e === "Cancel") this.dialogRef.close();
  }
  close() {
    this.dialogRef.close();
  }

  vendorDataSource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      this.gridRequestParams = params.request;
      this.apiLink.pipe(takeUntil(this.destroy$)).subscribe(
        (response: any) => {
          if (response) {
            if (this._activeTab == "PO's" || this._activeTab == "SKUs") {
              this.gridApi.hideOverlay();
              params.success({
                rowData: response,
                rowCount: response.length,
              });
              this.gridApi.sizeColumnsToFit();
            } else {
              if (!response.totalCount) {
                this.gridApi.showNoRowsOverlay();
              } else {
                this.gridApi.hideOverlay();
              }
              params.success({
                rowData: response.data,
                rowCount: response.totalCount,
              });
              this.gridApi.sizeColumnsToFit();
            }
          }
        },
        () => params.fail()
      );
    },
  };

  updateImage() {
    const value = this.productForm.get("imageUrl").value;

    if (value) {
      this.product.imageUrl = value;
    }
  }

  editItem(params): void {
    this.dialog
        .open(CreateVendorItemComponent, {
          width: "700px",
          panelClass: "dialog-container-custom",
          data: {
            productId: params.data.ItemId,
            vendorItem: params.data,
          },
        })
        .afterClosed()
        .pipe(filter((e) => e))
        .subscribe((e) => this.gridApi.refreshServerSide({ purge: true }));
  }

  deleteItem(params): void {
    this.dialog
      .open(ConfirmDialogComponent, {
          width: "500px",
          data: {
            title: "Delete Vendor Item",
            message: `Are you sure you want to remove ${params.node.data.VendorItemName} from this Product?`,
            warning: `<i class="far fa-exclamation-triangle me-2"></i>Note: If the item is on a PO, it will be archived, not deleted`,
          }
        })
        .afterClosed()
        .pipe(filter((e) => e === "Confirm"))
        .subscribe((e) => {
          this.inventoryService.RemoveVendorItem(params.data.ItemVendorId).pipe(takeUntil(this.destroy$)).subscribe(() =>
              this.gridApi.refreshServerSide({ purge: true }))
        })
  }

  editMapping(params: any): void {
    this.dialog
        .open(MapSkuToProductComponent, {
          width: "700px",
          panelClass: "dialog-container-custom",
          data: {
            sku: params.data
          },
        })
        .afterClosed()
        .pipe(filter(e => e))
        .subscribe((e: SetSkuMapPayload) => {
          this.planyardService.SetSkuMapping(e).pipe(takeUntil(this.destroy$)).subscribe(() => this.gridApi.refreshServerSide({ purge: true }));
        });
  }

  newVendorItem() {
    this.dialog
      .open(CreateVendorItemComponent, {
        width: "1000px",
        panelClass: "dialog-container-custom",
        data: {
          productId: this.productId,
        },
      })
      .afterClosed()
      .pipe(filter((e) => e))
      .subscribe((e) => {
        this.gridApi.refreshServerSide({ purge: true });
      });
  }

  newMapping() {
    this.dialog
      .open(MapSkuToProductComponent, {
        width: "700px",
        panelClass: "dialog-container-custom",
        data: {
          product: {
            ItemId: this.product.id,
            Name: this.product.name,
          },
        },
      })
      .afterClosed()
      .pipe(filter((e) => e))
      .subscribe((e: SetSkuMapPayload) => {
        this.planyardService.SetSkuMapping(e).pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.snackBar.openFromComponent(SnackbarComponent,
              {
                duration: 3000,
                horizontalPosition: 'center',
                data: {
                  title: 'Changes saved successfully',
                  action: 'Dismiss',
                  type: 'success',
                },
              }
          );
          this.gridApi.refreshServerSide({ purge: true });
        });
      });
  }
  
}
